import React from "react"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { IconSize } from "shared/types/Icon"
import { WithClassName } from "shared/types/WithClassName"
import { Icon } from "shared/ui/icon/Icon"
import { IconName } from "shared/ui/icon/__generated__/IconName"
import { Readonly } from "shared/ui/readonly/Readonly"

import { useFormattedPrice } from "entities/projects/model/useFormattedPrice"

import "./styles/_price-tag.scss"

export interface PriceTagProps extends WithClassName {
  price: number | undefined | null
  currency?: string
  isComplete?: boolean
  isLoading?: boolean
  isUpdating?: boolean
  isOverwritten?: boolean
  isPriceServiceAvailable?: boolean

  label?: React.ReactNode
  hideLabel?: boolean
  onPriceRequest?: () => void
  "data-testid"?: string
}

export const PriceTag = React.memo(
  React.forwardRef(
    (
      {
        price,
        currency,
        isComplete,
        label,
        hideLabel,
        isLoading,
        isUpdating,
        isOverwritten,
        className,
        isPriceServiceAvailable = true,
        onPriceRequest,
        ...rest
      }: PriceTagProps,
      ref: React.ForwardedRef<HTMLDivElement>
    ) => {
      const { t } = useTranslation()
      const { formattedPrice } = useFormattedPrice({
        price
      })

      return (
        <Readonly.Root
          {...rest}
          className={classNames(
            "price-tag",
            { "price-tag--updating": isUpdating },
            className
          )}
          isLoading={isLoading}
        >
          {!hideLabel ? (
            <Readonly.Label
              icon={isOverwritten ? IconName.USER_PEN : undefined}
            >
              {label || t("Price.Price")}
            </Readonly.Label>
          ) : null}
          <Readonly.Value>
            {!isPriceServiceAvailable ? (
              <div className="unavailability-message">
                <Icon
                  size={IconSize.SMALL}
                  name={IconName.CLOSE_CIRCLE}
                  className="color-danger"
                />
                {t("Price.ServiceUnavailable")}
              </div>
            ) : (
              <div ref={ref} className="price-tag__value">
                {formattedPrice}
              </div>
            )}
          </Readonly.Value>
        </Readonly.Root>
      )
    }
  )
)
